import type { ClientReportSnapshot } from '../../utils/client-reports/types';
import { LABEL_CLASS } from './ReportBlocks';
import PendingSettlementsSummaryView from './PendingSettlementsSummaryView';
import PendingSettlementsTableView from './PendingSettlementsTableView';
import PendingSettlementsByVesselView from './PendingSettlementsByVesselView';
import RecentShipmentsView from './RecentShipmentsView';
import ShipmentsToDateView from './ShipmentsToDateView';
import UpcomingArrivalsView from './UpcomingArrivalsView';
import { useReportLayout } from './reportLayout';
import { ReportLanguageContext, useReportFormat } from './useReportFormat';

const FRUITMARKETS_LOGO = '/fruitmarkets/logo-horizontal.png';
const FRUITMARKETS_LOGO_DARK = '/fruitmarkets/logo-horizontal-white.png';
// Misma caja para los dos logos: alto fijo y ancho máximo, sin deformarlos.
const LOGO_BOX_CLASS = 'h-14 w-auto max-w-[160px] object-contain';

// Las fotos anteriores a que hubiera más de un tipo no traen kind.
const REPORT_TITLES = {
  shipments: 'Informe de Despachos',
  pendingSettlements: 'Liquidaciones por recibir',
} as const;

// Dibuja un reporte a partir de su foto, en el idioma con que se emitió. Lo
// usan la vista previa, el link público y el PDF, así lo que se revisa antes
// de compartir es lo mismo que ve el cliente.
const ReportDocument = ({ snapshot }: { snapshot: ClientReportSnapshot }) => (
  <ReportLanguageContext.Provider value={snapshot.language ?? 'es'}>
    <ReportDocumentContent snapshot={snapshot} />
  </ReportLanguageContext.Provider>
);

const ReportDocumentContent = ({ snapshot }: { snapshot: ClientReportSnapshot }) => {
  const { t, date, localDate } = useReportFormat();
  const layout = useReportLayout();

  const facts = [
    {
      label: snapshot.receivers.length > 1 ? t('Recibidores') : t('Recibidor'),
      value: snapshot.receivers.join(' · '),
    },
    { label: t('Especie'), value: t(snapshot.speciesName) },
    { label: t('Temporada'), value: snapshot.season },
    // Rango cubierto hasta el corte y, aparte, cuándo se emitió. Las fotos
    // anteriores no traen corte: sus datos son al día en que se generaron.
    snapshot.cutoff
      ? {
          label: t('Rango cubierto'),
          value: t('Temporada hasta el {{date}}', {
            date: date(snapshot.cutoff.date),
          }),
          detail: [
            snapshot.cutoff.week != null
              ? t('Sem {{week}}', {
                  week: String(snapshot.cutoff.week).padStart(2, '0'),
                })
              : null,
            t('emitido el {{date}}', { date: localDate(snapshot.generatedAt) }),
          ]
            .filter(Boolean)
            .join(' · '),
        }
      : { label: t('Datos al'), value: localDate(snapshot.generatedAt) },
  ];

  return (
    <article className='flex flex-col gap-12 tabular-nums'>
      <header className='flex flex-col gap-6' data-pdf-block>
        {/* Logos en una fila y en una caja del mismo tamaño: exportadora a la
            izquierda y FruitMarkets a la derecha. El título va debajo. */}
        <div className='flex items-center justify-between gap-6'>
          {snapshot.exporterLogoUrl ? (
            <img
              src={snapshot.exporterLogoUrl}
              alt={snapshot.exporterName}
              className={`${LOGO_BOX_CLASS} object-left`}
            />
          ) : (
            <span />
          )}
          <div className='shrink-0'>
            <img
              src={FRUITMARKETS_LOGO}
              alt='FruitMarkets'
              className={`${LOGO_BOX_CLASS} object-right dark:hidden`}
            />
            <img
              src={FRUITMARKETS_LOGO_DARK}
              alt='FruitMarkets'
              className={`${LOGO_BOX_CLASS} hidden object-right dark:block`}
            />
          </div>
        </div>

        <div className='flex flex-col gap-1'>
          {/* Con el logo basta; el nombre solo si la exportadora no tiene logo. */}
          {snapshot.exporterLogoUrl ? null : (
            <span className={LABEL_CLASS}>{snapshot.exporterName}</span>
          )}
          {/* design-ignore: título del documento compartido (link público, vista previa y PDF), no de una página de la app */}
          <h2 className='text-2xl font-semibold tracking-tight'>
            {t(REPORT_TITLES[snapshot.kind ?? 'shipments'])}
          </h2>
        </div>

        <dl
          className={`grid gap-x-6 gap-y-3 border-y border-default-200 py-4 ${layout.kpiGrid}`}
        >
          {facts.map((fact) => (
            <div key={fact.label} className='flex min-w-0 flex-col gap-1'>
              <dt className={LABEL_CLASS}>{fact.label}</dt>
              <dd className='text-sm font-medium'>{fact.value}</dd>
              {'detail' in fact && fact.detail ? (
                <dd className='text-xs text-default-500'>{fact.detail}</dd>
              ) : null}
            </div>
          ))}
        </dl>
      </header>

      {snapshot.sections.map((section, index) => (
        <div key={`${section.type}-${index}`} data-pdf-section>
          {section.type === 'shipmentsToDate' ? (
            <ShipmentsToDateView number={index + 1} data={section.data} />
          ) : section.type === 'upcomingArrivals' ? (
            <UpcomingArrivalsView number={index + 1} data={section.data} />
          ) : section.type === 'recentShipments' ? (
            <RecentShipmentsView number={index + 1} data={section.data} />
          ) : section.type === 'pendingSettlementsSummary' ? (
            <PendingSettlementsSummaryView number={index + 1} data={section.data} />
          ) : section.type === 'settlementsByVessel' ? (
            <PendingSettlementsByVesselView number={index + 1} data={section.data} />
          ) : section.type === 'overdueSettlements' ? (
            <PendingSettlementsTableView
              number={index + 1}
              data={section.data}
              variant='overdue'
            />
          ) : section.type === 'settlementsDueSoon' ? (
            <PendingSettlementsTableView
              number={index + 1}
              data={section.data}
              variant='dueSoon'
            />
          ) : null}
        </div>
      ))}
    </article>
  );
};

export default ReportDocument;
